// apps/web/src/components/Home.jsx
import { Link } from 'react-router-dom';
import './Home.css';

// Adjust this path if your assets folder is located differently.
// This assumes: apps/web/src/assets/images/db-in-hands.png
import dbInHands from '../assets/images/db-in-hands.png';

const actionVideos = [
  {
    title: 'Search Everything',
    description: 'Find entries, attachments, and shared knowledge.',
    youtubeUrl: 'https://youtu.be/zymbfYBNafw?si=6_o2lbrlEQ7XY-Np',
  },
  {
    title: 'Preview Attachments',
    description: 'Open documents, spreadsheets, PDFs, and text files inline.',
    youtubeUrl: 'https://youtu.be/zymbfYBNafw?si=6_o2lbrlEQ7XY-Np',
  },
  {
    title: 'Sync Across Devices',
    description: 'Keep desktop files synchronized with MySimpleDB.',
    youtubeUrl: 'https://youtu.be/zymbfYBNafw?si=6_o2lbrlEQ7XY-Np',
  },
];

function getYouTubeEmbedUrl(url) {
  if (!url) return '';

  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes('youtu.be')) {
      const videoId = parsed.pathname.replace('/', '');
      return `https://www.youtube.com/embed/${videoId}`;
    }

    if (parsed.hostname.includes('youtube.com')) {
      const videoId = parsed.searchParams.get('v');
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }
  } catch {
    return '';
  }

  return '';
}

export default function Home() {
  return (
    <main className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero__content">
          <h1>Your data. Organized. Instantly.</h1>
          <p className="hero__tagline">
            Save, sync, search, and share notes, documents, and attachments—all
            from one place.
          </p>
          <div className="hero__cta">
            <Link to="/signup" className="btn btn-primary">
              Try Free
            </Link>
            <Link to="/login" className="btn btn-outline">
              Log In
            </Link>
          </div>
          <p className="hero__small">
            No credit card required • Free during beta
          </p>
        </div>

        <div className="hero__image" aria-hidden>
          <div className="mock-browser">
            <div className="mock-toolbar" />
            <div className="mock-window">
              {/* Image dropped into the framed “window” */}
              <img
                src={dbInHands}
                alt="Smiling person holding databases — MySimpleDB"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain', // ← show the whole image
                  objectPosition: 'center', // ← center it in the frame
                  display: 'block',
                  borderRadius: '16px',
                  background: '#f5f8fc', // optional: subtle backdrop behind letterboxing
                }}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip (sample logos) */}
      <section className="trust">
        <span>Trusted by folks at</span>
        <div className="trust__logos">
          <div className="logo-swatch" />
          <div className="logo-swatch" />
          <div className="logo-swatch" />
          <div className="logo-swatch" />
        </div>
      </section>

      {/* Features */}
      <section className="features" id="features">
        <header>
          <h2>Why MySimpleDB?</h2>
          <p>
            Everything you need to capture, find, and share info—without the
            clutter.
          </p>
        </header>

        <div className="grid grid-4">
          <article className="card">
            <div className="card__icon">🔎</div>
            <h3>Search Across Everything</h3>
            <p>
              Search your entries, attachments, and information shared with
              you—all from one search box.
            </p>
          </article>

          <article className="card">
            <div className="card__icon">📎</div>
            <h3>Attachments Preview</h3>
            <p>
              Store PDFs, images, spreadsheets, and documents with instant
              preview.
            </p>
          </article>

          <article className="card">
            <div className="card__icon">🔄</div>
            <h3>Sync Across Devices</h3>
            <p>
              Keep files synchronized automatically between desktop and web.
            </p>
          </article>

          <article className="card">
            <div className="card__icon">🤝</div>
            <h3>Sharing & Privacy</h3>
            <p>
              Keep entries private, make them public, or share securely with
              specific people.
            </p>
          </article>
        </div>
      </section>

      {/* How it works */}
      <section className="how" id="how">
        <header>
          <h2>How it works</h2>
          <p>Three simple steps to tame your info sprawl.</p>
        </header>

        <ol className="steps">
          <li>
            <span className="steps__num">1</span>
            <div>
              <h4>Save Everything</h4>
              <p>
                Capture notes, documents, images, and important information.
              </p>
            </div>
          </li>
          <li>
            <span className="steps__num">2</span>
            <div>
              <h4>Sync Everywhere</h4>
              <p>
                Keep attachments synchronized across your devices automatically.
              </p>
            </div>
          </li>
          <li>
            <span className="steps__num">3</span>
            <div>
              <h4>Find Anything</h4>
              <p>
                Search your data, attachments, and shared knowledge instantly.
              </p>
            </div>
          </li>
        </ol>
      </section>

      {/* Gallery teaser (sample content) */}
      <section className="gallery">
        <header>
          <h2>See it in action</h2>
        </header>

        <div className="grid grid-3">
          {actionVideos.map((video) => {
            const embedUrl = getYouTubeEmbedUrl(video.youtubeUrl);

            return (
              <article className="video-card" key={video.title}>
                <div className="video-card__frame">
                  {embedUrl ? (
                    <iframe
                      src={embedUrl}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <div className="video-card__placeholder">
                      Video coming soon
                    </div>
                  )}
                </div>

                <div className="video-card__body">
                  <h3>{video.title}</h3>
                  <p>{video.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="pricing" id="pricing">
        <header>
          <h2>Simple pricing</h2>
          <p>Start free. Upgrade when you’re ready.</p>
        </header>

        <div className="grid grid-3">
          <div className="plan card">
            <h3>Free</h3>
            <p className="price">$0</p>
            <ul>
              <li>Up to 500 entries</li>
              <li>Basic search</li>
              <li>5GB attachments</li>
            </ul>
            <Link to="/signup" className="btn btn-outline">
              Get Started
            </Link>
          </div>

          <div className="plan card plan--highlight">
            <h3>Pro</h3>
            <p className="price">
              $9<span>/mo</span>
            </p>
            <ul>
              <li>Unlimited entries</li>
              <li>Attachment search</li>
              <li>Shared knowledge access</li>
              <li>Desktop sync</li>
              <li>50GB attachments</li>
              <li>Priority support</li>
            </ul>
            <Link to="/signup" className="btn btn-primary">
              Start Pro Trial
            </Link>
          </div>

          <div className="plan card">
            <h3>Team</h3>
            <p className="price">Custom</p>
            <ul>
              <li>Team workspaces</li>
              <li>SSO & admin controls</li>
              <li>Priority support</li>
            </ul>
            <a href="mailto:sales@mysimpledb.com" className="btn btn-outline">
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      {/* CTA footer */}
      <section className="cta">
        <h2>Ready to organize everything?</h2>
        <div className="cta__actions">
          <Link to="/signup" className="btn btn-primary">
            Create your account
          </Link>
          <Link to="/login" className="btn btn-outline">
            Log in
          </Link>
        </div>
      </section>
    </main>
  );
}
